import {FC} from 'react';
import {Box, LinearProgress, Typography} from '@mui/material';
import {useIntl} from 'react-intl';
import {AiAgentUsage} from 'src/types';
import theme from 'src/theme';

interface UsageBarProps {
    usage: AiAgentUsage;
}

export const UsageBar: FC<UsageBarProps> = ({usage}) => {
    const intl = useIntl();
    const messagesPercent = Math.min((usage.messages_used / usage.messages_limit) * 100, 100);
    const tokensPercent = Math.min((usage.tokens_used / usage.tokens_limit) * 100, 100);
    const isNearLimit = messagesPercent > 80 || tokensPercent > 80;

    const formatNumber = (n: number) => n.toLocaleString();

    return (
        <Box sx={{
            px: 3,
            py: 1.5,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderTop: '1px solid rgba(0, 0, 0, 0.06)',
            display: 'flex',
            gap: 3,
            alignItems: 'center',
        }}>
            <Box sx={{flex: 1, minWidth: 0}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 0.5}}>
                    <Typography sx={{fontSize: '0.7rem', fontWeight: 600, color: theme.palette.neutral[500], textTransform: 'capitalize'}}>
                        {intl.formatMessage({id: 'aiAgentMessages'})}
                    </Typography>
                    <Typography sx={{fontSize: '0.7rem', color: theme.palette.neutral[400]}}>
                        {formatNumber(usage.messages_used)} / {formatNumber(usage.messages_limit)}
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={messagesPercent}
                    sx={{
                        height: 4,
                        borderRadius: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.06)',
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 2,
                            bgcolor: isNearLimit && messagesPercent > 80
                                ? theme.palette.warning.main
                                : theme.palette.primary.main,
                        },
                    }}
                />
            </Box>
            <Box sx={{flex: 1, minWidth: 0}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 0.5}}>
                    <Typography sx={{fontSize: '0.7rem', fontWeight: 600, color: theme.palette.neutral[500]}}>
                        {intl.formatMessage({id: 'aiAgentTokens'})}
                    </Typography>
                    <Typography sx={{fontSize: '0.7rem', color: theme.palette.neutral[400]}}>
                        {formatNumber(usage.tokens_used)} / {formatNumber(usage.tokens_limit)}
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={tokensPercent}
                    sx={{
                        height: 4,
                        borderRadius: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.06)',
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 2,
                            bgcolor: isNearLimit && tokensPercent > 80
                                ? theme.palette.warning.main
                                : theme.palette.primary.main,
                        },
                    }}
                />
            </Box>
        </Box>
    );
};